import {inject, Injectable} from '@angular/core';
import {ApiConfiguration} from '../restclient/api.config';
import {CheckEvaluation} from './app-data-checks';
import {
    type FormulaEvaluateColumnDataType,
    FormulaEvaluateRequest,
    FormulaEvaluateResponse,
    type FormulaEvaluateTableColumn,
    FormulaEvaluateTableInput,
    type FormulaEvaluateValue
} from '../../api-client-py/src';
import {AppComponent} from '../app.component';
import {AppConstants} from '../app.constants';
import JSZip from 'jszip';
import {AuthService} from '../security/auth.service';

export interface FormulaEvalResponse {
    requestId: string;
    durationMs: number;
    data: Record<string, any>[];
    checks: CheckEvaluation[];
    debugInformation?: FormulaEvalDebugInformation;
}

export interface FormulaEvalDebugExecutionProfile {
    step: string;
    durationMs: number;
}

export interface FormulaEvalDebugTable {
    name: string;
    columns: string[];
    rows: any[][];
}

export interface FormulaEvalDebugInformation {
    executionProfile: FormulaEvalDebugExecutionProfile[];
    tables: FormulaEvalDebugTable[];
    errors: string[];
}

/**
 * Service class for evaluating formulas using various input methods, values, and table data.
 * It's the bridge for the REST API endpoint `/formula/evaluate` in the Python service.
 * The service endpoint works very much as if an Excel formula evaluation cycle would execute.
 * In a single step, all the necessary input is sent to the service, and as a result, we receive
 * a list of tables and single cells that have been evaluated.
 */
@Injectable({
    providedIn: 'root'
})
export class AppDataFormulaService {

    private readonly authService = inject(AuthService);

    constructor() {
    }

    async evaluateFormula(
        methods: string[],
        values: FormulaEvaluateValue[],
        tables: FormulaEvaluateTableInput[],
        resultValues?: { [name : string]:  FormulaEvaluateColumnDataType } | null,
        resultTables?: { [name : string]:  FormulaEvaluateTableColumn[] } | null,
        abortSignal?: AbortSignal
    ): Promise<FormulaEvaluateResponse | undefined> {
        try {
            const gzip = AppConstants.getGzipRequests();
            if (gzip) {
                return await this.evaluateFormulaGzipFile(methods, values, tables, resultValues, resultTables, abortSignal);
            } else {
                return await this.evaluateFormulaPlain(methods, values, tables, resultValues, resultTables, abortSignal);
            }
        } catch (error) {
            // Handle error appropriately, e.g., log it or show an alert
            if (error instanceof DOMException && error.name === 'AbortError') {
                // Request was aborted, do nothing
                console.debug('Request aborted - no action taken');
                return undefined;
            } else {
                console.error(`Error evaluating formulas for '${methods}':`, error);
                const errorMessage = error instanceof Error ? error.message : String(error);
                AppComponent.messageToast.show({ message: errorMessage});
                // throw error; // Re-throw or handle as needed
                return undefined;
            }
        }
    }

    private authorizedHeader(headers: HeadersInit): HeadersInit {
        const accessToken = this.authService.accessToken();
        if (accessToken) {
            return { ...headers, Authorization: `Bearer ${accessToken}`};
        } else {
            return headers;
        }
    }

    private async evaluateFormulaPlain(
        methods: string[],
        values: FormulaEvaluateValue[],
        tables: FormulaEvaluateTableInput[],
        resultValues?: { [name : string]:  FormulaEvaluateColumnDataType } | null,
        resultTables?: { [name : string]:  FormulaEvaluateTableColumn[] } | null,
        abortSignal?: AbortSignal
    ): Promise<FormulaEvaluateResponse | undefined> {
        const requestId = crypto.randomUUID();

        const evalRequest: FormulaEvaluateRequest = {
            requestId: requestId,
            methods: methods,
            tables: tables,
            values: values,
            results: { tables: resultTables, values: resultValues }
        }

        const requestJson = JSON.stringify(evalRequest);
        const gzip = false;

        let body;
        if (gzip) {
            const stream = new Blob([requestJson]).stream();
            const compressedReadableStream = stream.pipeThrough(new CompressionStream("gzip"));
            const compressedRequest = new Response(compressedReadableStream);
            body = await compressedRequest.blob();
        } else {
            body = requestJson;
        }

        const headers: any = {
            'Content-Type': 'application/json',
            'Content-Encoding': gzip ? 'gzip' : undefined,
            'X-Request-ID': requestId
        };

        const url = `${ApiConfiguration.getBasePathPy()}/formula/evaluate`;
        const accountsResponse = await fetch(url, {
            method: 'POST',
            headers: this.authorizedHeader(headers),
            signal: abortSignal,
            body: body
        });

        return await accountsResponse.json() as FormulaEvalResponse;
    }

    private async evaluateFormulaGzipFile(
        methods: string[],
        values: FormulaEvaluateValue[],
        tables: FormulaEvaluateTableInput[],
        resultValues?: { [name : string]:  FormulaEvaluateColumnDataType } | null,
        resultTables?: { [name : string]:  FormulaEvaluateTableColumn[] } | null,
        abortSignal?: AbortSignal
    ): Promise<FormulaEvaluateResponse | undefined> {
        const requestId = crypto.randomUUID();

        const evalRequest: FormulaEvaluateRequest = {
            requestId: requestId,
            methods: methods,
            tables: tables,
            values: values,
            results: { tables: resultTables, values: resultValues }
        }

        const requestJson = JSON.stringify(evalRequest);
        const stream = new Blob([requestJson]).stream();
        const compressedReadableStream = stream.pipeThrough(new CompressionStream("gzip"));
        const compressedRequest = new Response(compressedReadableStream);
        const compressedBlob = await compressedRequest.blob();

        // Create a real File object with the desired filename
        const gzipFile = new File([compressedBlob], 'formula.gz', {
            type: 'application/gzip'
        });

        // Build multipart/form-data body
        const formData = new FormData();
        formData.append('file', gzipFile); // <- field name may need to match backend expectation
        formData.append('requestId', requestId); // optional, if backend reads it from form

        const url = `${ApiConfiguration.getBasePathPy()}/formula/evaluate/gzip`;
        const headers = {
            'X-Request-ID': requestId
            // IMPORTANT: don't set Content-Type manually for FormData
        };

        const accountsResponse = await fetch(url, {
            method: 'POST',
            signal: abortSignal,
            headers: this.authorizedHeader(headers),

            body: formData
        });

        return await accountsResponse.json() as FormulaEvalResponse;
    }
}
